import React, { useState, useEffect, useRef } from 'react';
import { Star, Heart, Lock, Unlock, Zap, ChevronRight, Gift, Music, Clock, Grid, Brain, ShieldCheck, Activity } from 'lucide-react';

/**
 * GYMKANA ANA 4.0 - VERSIÓN EXTENDIDA (5 DÍAS)
 * * ESTRUCTURA:
 * Día 1: Identidad (Quiz) + Físico (Runner Easy)
 * Día 2: Resistencia (Quiz) + Físico (Runner Hard)
 * Día 3: Memoria (Parejas)
 * Día 4: Agilidad Mental (Simón Dice / Secuencia) -> NUEVO
 * Día 5: Recompensa Final
 * * NOTA PARA DESPLIEGUE:
 * Asegúrate de tener Tailwind CSS configurado en tu proyecto.
 */

const Ana40Experience = () => {
  // Estado global: Día actual (1-5)
  const [currentDay, setCurrentDay] = useState(1);
  const [appState, setAppState] = useState('intro'); // intro, challenge, game, wait, final
  const [foxClicks, setFoxClicks] = useState(0);
  const [showAdmin, setShowAdmin] = useState(false);

  // Persistencia de datos
  useEffect(() => {
    const savedDay = localStorage.getItem('ana40_day');
    if (savedDay) setCurrentDay(parseInt(savedDay));
  }, []);

  // --- COMPONENTES AUXILIARES ---
  const DayBadge = () => (
    <div className="absolute top-6 right-6 z-20 bg-purple-900/80 backdrop-blur border border-purple-500/50 px-4 py-2 rounded-full text-xs font-mono text-purple-300 shadow-[0_0_15px_rgba(168,85,247,0.3)]">
      DÍA {currentDay}/5
    </div>
  );

  // --- PANTALLAS ---

  // 1. INTRODUCCIÓN DIARIA
  const IntroScreen = ({ onStart, onFoxClick }) => {
    const texts = {
      1: { title: "NIVEL 40", sub: "Inicializando sistema operativo...", btn: "Comenzar Aventura" },
      2: { title: "DÍA 2", sub: "El sistema ha detectado anomalías. Se requiere más habilidad.", btn: "Continuar" },
      3: { title: "DÍA 3", sub: "Módulo de Memoria dañado. Necesitamos reparación manual.", btn: "Acceder a Memoria" },
      4: { title: "DÍA 4", sub: "Protocolo de Seguridad activado. Desactiva la alarma.", btn: "Hackear Sistema" },
      5: { title: "ACCESO TOTAL", sub: "Todos los cortafuegos eliminados.", btn: "Ver Archivos Secretos" }
    };

    const t = texts[currentDay] || texts[1];

    return (
      <div className="flex flex-col items-center justify-center h-full text-center space-y-10 animate-fadeIn p-6 relative z-10">
        <DayBadge />
        <div
          onClick={onFoxClick}
          className="relative text-9xl cursor-pointer select-none transition-transform active:scale-90 hover:scale-110 hover:rotate-6 filter drop-shadow-[0_0_25px_rgba(192,132,252,0.6)] animate-float"
        >
          {currentDay === 5 ? '🎁' : '🦊'}
        </div>

        <div className="space-y-4 max-w-lg">
          <h1 className="text-5xl md:text-6xl font-thin text-transparent bg-clip-text bg-gradient-to-r from-purple-200 via-pink-200 to-purple-200 tracking-widest leading-tight">
            {t.title}
          </h1>
          <p className="text-purple-200 text-lg font-light tracking-wide">
            {t.sub}
          </p>
        </div>

        <button
          onClick={onStart}
          className="group relative px-10 py-4 bg-transparent border border-purple-400/50 text-purple-100 rounded-full overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] hover:border-purple-300 active:scale-95"
        >
          <div className="absolute inset-0 bg-purple-600/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          <span className="relative z-10 flex items-center gap-3 uppercase tracking-[0.2em] text-sm font-bold">
            {t.btn} <ChevronRight className="w-4 h-4" />
          </span>
        </button>
      </div>
    );
  };

  // 2. QUIZ / PREGUNTAS (Días 1 y 2)
  const ChallengeScreen = ({ onComplete }) => {
    const [error, setError] = useState(false);

    const content = {
      1: {
        q: "Confirma tu identidad. ¿Qué bebida no puede faltar en nuestras reuniones?",
        options: [
          { text: "Agua mineral (somos fitness)", correct: false },
          { text: "Vino / Cerveza (somos felices)", correct: true }
        ]
      },
      2: {
        q: "Un clásico. ¿En qué año dijimos 'nunca más' y lo volvimos a hacer?",
        options: [
          { text: "Cada maldito fin de semana", correct: true },
          { text: "En 2010", correct: false }
        ]
      }
    };

    const currentQ = content[currentDay] || content[1];

    const handleAnswer = (isCorrect) => {
      if (isCorrect) {
        onComplete();
      } else {
        setError(true);
        setTimeout(() => setError(false), 800);
      }
    };

    return (
      <div className="w-full max-w-md p-6">
        <h2 className="text-xl text-purple-300 text-center mb-10 uppercase tracking-[0.3em] opacity-80">
          Control de Acceso
        </h2>
        <div className={`bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-3xl transition-all duration-300 ${error ? 'border-red-500/50 translate-x-2 shadow-[0_0_20px_rgba(239,68,68,0.3)]' : 'shadow-[0_0_40px_rgba(139,92,246,0.1)]'}`}>
          <p className="text-xl text-white mb-8 text-center font-light leading-relaxed">{currentQ.q}</p>
          <div className="space-y-4">
            {currentQ.options.map((opt, i) => (
              <button key={i} onClick={() => handleAnswer(opt.correct)} className="w-full p-5 bg-purple-900/40 hover:bg-purple-800/60 border border-purple-500/20 rounded-xl text-left transition-all active:scale-95 flex justify-between group">
                <span className="text-purple-100">{opt.text}</span>
                <ChevronRight className="text-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 3. JUEGO: FOX RUNNER (Días 1 y 2)
  const FoxGame = ({ difficulty, onSuccess }) => {
    const [gameState, setGameState] = useState('ready');
    const [score, setScore] = useState(0);
    const foxRef = useRef(null);
    const obstacleRef = useRef(null);
    const requestRef = useRef();

    const SPEED = difficulty === 'hard' ? '0.9s' : '1.5s';
    const WIN_SCORE = difficulty === 'hard' ? 8 : 4;
    const OBSTACLE_ICON = difficulty === 'hard' ? '🔥' : '🌵';

    const jump = () => {
      if (gameState !== 'playing') {
        if (gameState === 'ready' || gameState === 'lost') {
          setGameState('playing');
          setScore(0);
        }
        return;
      }
      if (!foxRef.current?.classList.contains('jumping')) {
        foxRef.current.classList.add('jumping');
        setTimeout(() => foxRef.current?.classList.remove('jumping'), 600);
      }
    };

    useEffect(() => {
      if (gameState !== 'playing') return;

      const checkCollision = () => {
        const fox = foxRef.current;
        const obs = obstacleRef.current;
        if (!fox || !obs) return;

        const fRect = fox.getBoundingClientRect();
        const oRect = obs.getBoundingClientRect();

        // Colisión simple
        if (oRect.left < fRect.right - 25 && oRect.right > fRect.left + 25 && fRect.bottom > oRect.top + 15) {
          setGameState('lost');
        }

        // Sumar puntos
        if (oRect.right < fRect.left && !obs.passed) {
          obs.passed = true;
          setScore(s => {
            const newS = s + 1;
            if (newS >= WIN_SCORE) {
              setGameState('won');
              setTimeout(onSuccess, 1500);
            }
            return newS;
          });
          setTimeout(() => { if (obs) obs.passed = false }, 1000);
        }
        requestRef.current = requestAnimationFrame(checkCollision);
      };
      requestRef.current = requestAnimationFrame(checkCollision);
      return () => cancelAnimationFrame(requestRef.current);
    }, [gameState]);

    return (
      <div className="w-full h-full flex flex-col items-center justify-between py-12 touch-none select-none" onMouseDown={jump} onTouchStart={jump}>
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-900/50 border border-purple-500/30 mb-2">
            <Activity size={14} className="text-green-400 animate-pulse" />
            <span className="text-[10px] tracking-widest uppercase text-purple-200">
              {difficulty === 'hard' ? 'Modo: Pesadilla' : 'Modo: Normal'}
            </span>
          </div>
          <div className="text-6xl font-black text-white drop-shadow-[0_0_15px_rgba(168,85,247,0.5)]">{score}/{WIN_SCORE}</div>
        </div>

        <div className="w-full h-60 relative border-b-2 border-purple-500/50 overflow-hidden bg-gradient-to-t from-purple-900/20 to-transparent">
          {gameState === 'ready' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-10">
              <span className="text-white font-bold animate-pulse px-6 py-3 bg-purple-600 rounded-full shadow-lg">TOCA PARA SALTAR</span>
            </div>
          )}
          {gameState === 'lost' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm z-20">
              <span className="text-red-400 font-bold text-3xl mb-2">¡OUCH!</span>
              <span className="text-sm text-white/70">Toca para reintentar</span>
            </div>
          )}
          {gameState === 'won' && (
            <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 backdrop-blur-md z-20">
              <span className="text-white font-bold text-3xl animate-bounce">¡SUPERADO!</span>
            </div>
          )}

          <div ref={foxRef} className="absolute bottom-0 left-6 text-7xl z-10 filter drop-shadow-lg">🦊</div>
          <div
            ref={obstacleRef}
            className={`absolute bottom-2 -right-16 text-5xl ${gameState === 'playing' ? 'animate-obstacle' : ''}`}
            style={{ animationDuration: SPEED }}
          >
            {OBSTACLE_ICON}
          </div>
        </div>
        <p className="text-xs text-purple-400/50 mt-4 tracking-widest uppercase">Toca la pantalla para esquivar</p>
      </div>
    );
  };

  // 4. JUEGO: MEMORY (Día 3)
  const MemoryGame = ({ onSuccess }) => {
    const ICONS = ['🦊', '🍷', '👠', '👑', '💄', '📱'];
    const [cards, setCards] = useState([]);
    const [flipped, setFlipped] = useState([]);
    const [solved, setSolved] = useState([]);

    useEffect(() => {
      const deck = [...ICONS, ...ICONS].sort(() => Math.random() - 0.5).map((icon, id) => ({ id, icon }));
      setCards(deck);
    }, []);

    const handleCardClick = (id) => {
      if (flipped.length === 2 || solved.includes(id) || flipped.includes(id)) return;
      const newFlipped = [...flipped, id];
      setFlipped(newFlipped);
      if (newFlipped.length === 2) {
        const [a, b] = newFlipped;
        if (cards[a].icon === cards[b].icon) {
          setSolved([...solved, a, b]);
          setFlipped([]);
          if (solved.length + 2 === cards.length) setTimeout(onSuccess, 1000);
        } else {
          setTimeout(() => setFlipped([]), 1000);
        }
      }
    };

    return (
      <div className="p-4 w-full max-w-md">
        <h3 className="text-center text-purple-200 mb-8 uppercase tracking-[0.2em] text-sm">Restaurando Memoria...</h3>
        <div className="grid grid-cols-3 gap-4">
          {cards.map((card, i) => (
            <div
              key={i}
              onClick={() => handleCardClick(i)}
              className={`aspect-square rounded-2xl flex items-center justify-center text-3xl cursor-pointer transition-all duration-500 transform perspective-1000 ${flipped.includes(i) || solved.includes(i)
                  ? 'bg-gradient-to-br from-purple-500 to-pink-600 rotate-y-180 shadow-[0_0_20px_rgba(168,85,247,0.6)]'
                  : 'bg-white/5 border border-white/10 hover:bg-white/10'
                }`}
            >
              <div className={flipped.includes(i) || solved.includes(i) ? 'block' : 'hidden animate-fadeIn'}>
                {card.icon}
              </div>
              <div className={!(flipped.includes(i) || solved.includes(i)) ? 'block text-purple-500/30' : 'hidden'}>?</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 5. JUEGO: SIMON SAYS / SECUENCIA (Día 4 - NUEVO)
  const SimonGame = ({ onSuccess }) => {
    const [sequence, setSequence] = useState([]);
    const [playing, setPlaying] = useState(false);
    const [userStep, setUserStep] = useState(0);
    const [message, setMessage] = useState("Iniciar Hackeo");
    const [flash, setFlash] = useState(null); // Color que brilla

    const colors = [
      { id: 'red', color: 'bg-red-500', sound: 200 },
      { id: 'green', color: 'bg-green-500', sound: 300 },
      { id: 'blue', color: 'bg-blue-500', sound: 400 },
      { id: 'yellow', color: 'bg-yellow-500', sound: 500 }
    ];

    const WIN_ROUND = 5;

    const playSequence = async (seq) => {
      setPlaying(false); // Bloquear input
      setMessage("Observa el patrón...");

      for (let i = 0; i < seq.length; i++) {
        await new Promise(r => setTimeout(r, 500));
        setFlash(seq[i]);
        await new Promise(r => setTimeout(r, 400));
        setFlash(null);
      }

      setMessage("¡Repite la secuencia!");
      setPlaying(true); // Desbloquear input
    };

    const startGame = () => {
      const startSeq = [colors[Math.floor(Math.random() * 4)].id];
      setSequence(startSeq);
      setUserStep(0);
      playSequence(startSeq);
    };

    const handleTap = (colorId) => {
      if (!playing) return;

      // Efecto visual instantáneo
      setFlash(colorId);
      setTimeout(() => setFlash(null), 200);

      // Lógica
      if (colorId === sequence[userStep]) {
        // Correcto
        if (userStep === sequence.length - 1) {
          // Ronda completada
          if (sequence.length === WIN_ROUND) {
            setMessage("¡HACKEO COMPLETADO!");
            setPlaying(false);
            setTimeout(onSuccess, 1500);
          } else {
            setMessage("¡Correcto! Aumentando dificultad...");
            setPlaying(false);
            setUserStep(0);
            setTimeout(() => {
              const nextSeq = [...sequence, colors[Math.floor(Math.random() * 4)].id];
              setSequence(nextSeq);
              playSequence(nextSeq);
            }, 1000);
          }
        } else {
          setUserStep(userStep + 1);
        }
      } else {
        // Fallo
        setMessage("¡ERROR! Reiniciando...");
        setPlaying(false);
        setSequence([]);
        setTimeout(startGame, 1500);
      }
    };

    useEffect(() => {
      // Iniciar juego al montar
      setTimeout(startGame, 1000);
    }, []);

    return (
      <div className="w-full max-w-md p-6 flex flex-col items-center">
        <div className="flex items-center gap-2 mb-8 text-purple-300">
          <ShieldCheck className="animate-pulse" />
          <span className="uppercase tracking-widest text-sm">Sistema de Seguridad</span>
        </div>

        <div className="text-xl text-white font-bold mb-8 h-8">{message}</div>

        <div className="grid grid-cols-2 gap-4 w-full max-w-[300px]">
          {colors.map((c) => (
            <div
              key={c.id}
              onClick={() => handleTap(c.id)}
              className={`
                aspect-square rounded-2xl border-4 border-black/20 shadow-2xl transition-all duration-100 cursor-pointer active:scale-95
                ${c.color}
                ${flash === c.id ? 'brightness-150 scale-105 shadow-[0_0_30px_rgba(255,255,255,0.8)]' : 'opacity-60 hover:opacity-80'}
              `}
            />
          ))}
        </div>

        <div className="mt-8 text-xs text-purple-500/50 font-mono">
          SECUENCIA NIVEL: {sequence.length} / {WIN_ROUND}
        </div>
      </div>
    );
  };

  // 6. PANTALLA DE ESPERA
  const WaitScreen = () => (
    <div className="text-center p-8 animate-fadeIn max-w-md">
      <div className="inline-block p-6 rounded-full bg-purple-900/30 mb-6 border border-purple-500/30">
        <Clock className="w-12 h-12 text-purple-400 animate-pulse" />
      </div>
      <h2 className="text-3xl font-light text-white mb-4">Progreso Guardado</h2>
      <p className="text-purple-200 mb-8 leading-relaxed">
        Has superado el reto de hoy. Descansa, agente.
        Mañana se desbloqueará el siguiente nivel.
      </p>
      <div className="w-full bg-black/40 h-2 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
          style={{ width: `${(currentDay / 5) * 100}%` }}
        ></div>
      </div>
      <p className="mt-2 text-xs text-purple-500 uppercase tracking-widest">Cargando día {currentDay + 1}...</p>
    </div>
  );

  // 7. PANTALLA FINAL (Día 5)
  const FinalScreen = () => (
    <div className="max-w-md p-8 text-center animate-fadeIn relative z-10">
      <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-purple-500/10 to-transparent blur-3xl -z-10"></div>

      <Gift className="w-24 h-24 text-pink-400 mx-auto mb-8 animate-bounce filter drop-shadow-[0_0_20px_rgba(236,72,153,0.4)]" />

      <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-200 mb-6">
        ¡MISIÓN CUMPLIDA!
      </h1>

      <p className="text-lg text-purple-100 mb-8 leading-relaxed font-light">
        Bienvenida oficialmente al Club de los 40.<br />
        Has demostrado agilidad, memoria y tenacidad.
      </p>

      <div className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 shadow-2xl transform hover:scale-105 transition-transform">
        <h3 className="text-xs uppercase tracking-[0.3em] text-purple-300 mb-4 border-b border-white/10 pb-2">UBICACIÓN DEL TESORO</h3>
        <p className="text-2xl font-medium text-white italic font-serif">
          "Mira debajo del sofá. Sí, ahí donde nunca limpiamos."
        </p>
      </div>
    </div>
  );

  // --- CONTROLADOR ---

  const handleNextStep = () => {
    // Día 5 es solo pantalla final
    if (currentDay === 5) return;

    if (appState === 'intro') {
      // Días 1 y 2 van a Quiz
      if (currentDay <= 2) setAppState('challenge');
      // Día 3 va directo a Memory
      if (currentDay === 3) setAppState('game');
      // Día 4 va directo a Simon
      if (currentDay === 4) setAppState('game');
    }
    else if (appState === 'challenge') {
      setAppState('game');
    }
    else if (appState === 'game') {
      setAppState('wait');
    }
  };

  // --- ADMIN PANEL (Para ti) ---
  const AdminPanel = () => (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center p-6 text-green-500 font-mono text-sm">
      <div className="border border-green-500/30 p-6 rounded-lg max-w-sm w-full bg-black">
        <h2 className="text-xl mb-6 border-b border-green-500/50 pb-2 text-center">GOD MODE</h2>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(d => (
            <button
              key={d}
              onClick={() => {
                setCurrentDay(d);
                setAppState(d === 5 ? 'final' : 'intro');
                setShowAdmin(false);
                setFoxClicks(0);
              }}
              className={`w-full py-3 border border-green-500/30 hover:bg-green-500/10 transition-colors ${currentDay === d ? 'bg-green-900/30 text-white border-green-500' : ''}`}
            >
              WARP TO DAY {d}
            </button>
          ))}
        </div>
        <button onClick={() => setShowAdmin(false)} className="mt-6 w-full text-center text-red-500 hover:text-red-400">[ CLOSE TERMINAL ]</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0f0518] text-white font-sans overflow-hidden relative selection:bg-purple-500">

      {/* Background Dinámico */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[70%] h-[70%] bg-purple-900/20 rounded-full blur-[120px] animate-blob" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[70%] h-[70%] bg-indigo-900/20 rounded-full blur-[120px] animate-blob animation-delay-2000" />
        <div className="absolute top-[30%] left-[30%] w-[40%] h-[40%] bg-pink-900/10 rounded-full blur-[100px] animate-blob animation-delay-4000" />
      </div>

      <style jsx global>{`
        @keyframes float { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-15px) rotate(5deg); } }
        @keyframes blob { 0% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } 100% { transform: translate(0px, 0px) scale(1); } }
        @keyframes obstacle-move { from { right: -50px; } to { right: 120%; } }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-blob { animation: blob 20s infinite; }
        .animate-obstacle { animation: obstacle-move linear infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
        .jumping { animation: jump-anim 0.6s ease-out; }
        @keyframes jump-anim { 0% { transform: translateY(0); } 50% { transform: translateY(-120px) rotate(-10deg); } 100% { transform: translateY(0); } }
      `}</style>

      {/* Main Container */}
      <main className="relative z-10 h-screen flex flex-col">
        {/* Progress Bar Header */}
        <header className="px-6 py-4 flex justify-between items-center bg-black/10 backdrop-blur-sm border-b border-white/5">
          <span className="text-[10px] tracking-[0.2em] uppercase text-purple-400 font-bold">Project: ANA 4.0</span>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map(d => (
              <div
                key={d}
                className={`h-1.5 rounded-full transition-all duration-500 ${d <= currentDay ? 'w-6 bg-purple-400 shadow-[0_0_10px_rgba(168,85,247,0.5)]' : 'w-2 bg-purple-900/30'}`}
              />
            ))}
          </div>
        </header>

        {/* Dynamic Content */}
        <div className="flex-1 flex items-center justify-center p-4">

          {appState === 'intro' && (
            <IntroScreen
              onStart={handleNextStep}
              onFoxClick={() => {
                const newClicks = foxClicks + 1;
                setFoxClicks(newClicks);
                if (newClicks === 5) setShowAdmin(true);
              }}
            />
          )}

          {appState === 'challenge' && (
            <ChallengeScreen onComplete={handleNextStep} />
          )}

          {appState === 'game' && currentDay <= 2 && (
            <FoxGame difficulty={currentDay === 2 ? 'hard' : 'easy'} onSuccess={handleNextStep} />
          )}

          {appState === 'game' && currentDay === 3 && (
            <MemoryGame onSuccess={handleNextStep} />
          )}

          {appState === 'game' && currentDay === 4 && (
            <SimonGame onSuccess={handleNextStep} />
          )}

          {appState === 'wait' && <WaitScreen />}

          {appState === 'final' && <FinalScreen />}

        </div>

        {/* Footer */}
        <footer className="p-4 text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/10">Designed for Ana • {new Date().getFullYear()}</p>
        </footer>
      </main>

      {/* Admin Overlay */}
      {showAdmin && <AdminPanel />}
    </div>
  );
};

export default Ana40Experience;